/**
 * The community heatmap on the print page preview.
 *
 * Mirrors what the renderer draws (see _print_sheet_controller.html.erb):
 * the raster tiles straight over the cartography at one fixed opacity, with
 * none of the dark ground layer the web map puts underneath — that would
 * print as a near-black sheet, so the preview leaves it out too and shows
 * what will actually come off the printer.
 */
import { MapLayerCategory } from '../layer-registry';
import type { LayerRegistry } from '../layer-registry';
import { HEATMAP_NATIVE_MAXZOOM, HEATMAP_TILE_SIZE, HEATMAP_TILE_URL } from '../heatmapTiles';

export const PRINT_HEATMAP_SOURCE_ID = 'print-heatmap-source';
export const PRINT_HEATMAP_LAYER_ID = 'print-heatmap-layer';
/** Matches PrintMaps::DocumentPresenter::HEATMAP_OPACITY. */
export const PRINT_HEATMAP_OPACITY = 0.85;

/** The key BaseMapDataOverlaysModule persists the web overlay toggle under. */
export const HEATMAP_ACTIVE_STORAGE_KEY = 'heatmapActive';

/**
 * Whether the heatmap switch starts on: it follows the main map, so someone
 * who was browsing with the heatmap on and pressed Print gets it on the
 * sheet without hunting for the option.
 *
 * Storage can throw (private browsing, blocked site data) or hold anything,
 * so every failure reads as off — the option then simply starts unticked.
 */
export function initialShowHeatmap(storage: Pick<Storage, 'getItem'> | null | undefined): boolean {
  try {
    const raw = storage?.getItem(HEATMAP_ACTIVE_STORAGE_KEY);
    return raw ? JSON.parse(raw) === true : false;
  } catch {
    return false;
  }
}

type HeatmapRegistry = Pick<LayerRegistry, 'addLayer' | 'removeLayer'>;

/** Adds or removes the preview layer to match the switch. */
export function syncPrintHeatmap(registry: HeatmapRegistry | null | undefined, enabled: boolean): void {
  if (!registry) return;

  if (!enabled) {
    registry.removeLayer(PRINT_HEATMAP_LAYER_ID);
    return;
  }

  registry.addLayer(
    {
      id: PRINT_HEATMAP_LAYER_ID,
      type: 'raster',
      source: PRINT_HEATMAP_SOURCE_ID,
      paint: { 'raster-opacity': PRINT_HEATMAP_OPACITY },
    },
    MapLayerCategory.Overlays,
    {
      z: 3,
      replace: true,
      belowLabels: true,
      source: {
        id: PRINT_HEATMAP_SOURCE_ID,
        spec: {
          type: 'raster',
          tiles: [HEATMAP_TILE_URL],
          tileSize: HEATMAP_TILE_SIZE,
          minzoom: 0,
          maxzoom: HEATMAP_NATIVE_MAXZOOM,
        },
      },
    },
  );
}
